import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import { useWorkspaceJobAssignmentsQuery } from './workspaceApi';
import { useWorkspaceScope } from './useWorkspaceScope';
import { workspaceAgentPath } from './workspacePaths';

type Assignment = {
  id: string;
  name: string;
  campaigns: { id: string; title: string; status: string }[];
};

export const JobAssignmentsDialog = ({
  id,
  name,
  onClose,
}: {
  id: string;
  name: string;
  onClose: () => void;
}) => {
  const scope = useWorkspaceScope();
  return (
    <AssignmentSession key={`${scope}:${id}`} id={id} name={name} scope={scope} onClose={onClose} />
  );
};

const AssignmentSession = ({
  id,
  name,
  scope,
  onClose,
}: {
  id: string;
  name: string;
  scope: string;
  onClose: () => void;
}) => {
  const workspace = useDesignWorkspace();
  const [after, setAfter] = useState<string>();
  const [previous, setPrevious] = useState<Assignment[]>([]);
  const query = useWorkspaceJobAssignmentsQuery(
    { scope, id, after },
    { refetchOnMountOrArgChange: true },
  );
  const data = query.currentData;
  const agents = [
    ...new Map([...previous, ...(data?.agents || [])].map((agent) => [agent.id, agent])).values(),
  ];
  const next = data?.next;
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} aria-labelledby="job-assignments-title">
      <DialogTitle id="job-assignments-title">Assistants using {name}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography color="text.secondary">
            Current company assignments to this shared job. Opening an assistant retains its
            campaign scope.
          </Typography>
          {data && (
            <Typography role="status">
              {data.total} {data.total === 1 ? 'assistant' : 'assistants'} · {agents.length} loaded
            </Typography>
          )}
          {query.isError && (
            <Alert severity="error">
              Assignments could not be loaded. Earlier loaded rows are retained.{' '}
              <Button onClick={() => void query.refetch()}>Retry assignments</Button>
            </Alert>
          )}
          {query.isFetching && <CircularProgress size={22} aria-label="Loading job assignments" />}
          {agents.map((agent) => (
            <Paper variant="outlined" key={agent.id} sx={{ p: 2 }}>
              <Stack gap={1}>
                <Typography fontWeight={600}>{agent.name}</Typography>
                {agent.campaigns.map((campaign) => (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    gap={1}
                    key={campaign.id}>
                    <Typography variant="body2">
                      {campaign.title} · {campaign.status}
                    </Typography>
                    <Button
                      onClick={() => workspace.navigate(workspaceAgentPath(campaign.id, agent.id))}>
                      Open {agent.name} in {campaign.title}
                    </Button>
                  </Stack>
                ))}
                {!agent.campaigns.length && (
                  <Typography color="text.secondary">
                    No current campaign assignment is available.
                  </Typography>
                )}
              </Stack>
            </Paper>
          ))}
          {!query.isFetching && !query.isError && data?.total === 0 && (
            <Alert severity="info">No assistants currently use this job.</Alert>
          )}
          {next && (
            <Button
              disabled={query.isFetching || query.isError}
              onClick={() => {
                if (!data || !next) return;
                setPrevious(agents);
                setAfter(next);
              }}>
              Load more assignments
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close assignments</Button>
      </DialogActions>
    </Dialog>
  );
};
